/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react'
import { Form, Field } from 'react-final-form'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router-dom'
import {
  Button,
  FormControl,
  FormControlLabel,
  MenuItem,
  TextField,
  InputLabel,
  makeStyles,
} from '@material-ui/core'
import UploadIcon from '@material-ui/icons/CloudUpload'
import ClearIcon from '@material-ui/icons/Clear'
import { injectIntl } from 'react-intl'
import { isEmpty, isEqual } from 'lodash'
import { Checkbox, Select } from 'components/common/FormInputs'
import getValidator, {
  VALIDATOR_IS_EMPTY,
  VALIDATOR_IS_LESS_THEN_255_SYMBOLS,
  VALIDATOR_IS_NUMBER,
  VALIDATOR_IS_YOUTUBE_LINK,
} from 'validation'
import TitleInput from 'components/common/TitleInput/TitleInput'
import TextArea from 'components/common/Form/TextArea'
import { uploadFiles } from 'reducers/common/files/reducer'
import { fetchAssignData } from 'reducers/myJobs/reducer'
import CircleLoader from 'components/Loading/CircleLoader'
import Input from 'components/common/Form/Input'
import NoticeHelper from 'helpers/notice'
import withPromise from 'helpers/withPromise'
import Psfs from 'lib/psfs/Psfs'
import { MY_JOBS_UPLOAD_FILES } from 'constants/apiRouting'
import {
  fetchWorkflowList,
  fetchTaskFormData,
  fetchTaskPolicies,
} from 'reducers/jobTasksAdmin/reducer'
import { fetchLookupTablesSource } from 'reducers/jobInputsAdmin/reducer'
import { APP_LANGUAGES } from 'constants/index'
import { REACT_INTL_PROP_TYPE } from 'constants/propTypes'
import TextEditor from 'components/common/TextEditor'
import {
  LOCUS_LOOKUP_TABLE,
  LOCUS_LOOKUP_TABLE_COLUMN,
  LOCUS_CREATE_NEW_ROW,
  LOCUS_GROUP_LOOKUP_TABLE,
  LOCUS_GROUP_LOOKUP_TABLE_COLUMN,
  LOCUS_GROUP_CREATE_NEW_ROW,
} from './LookupTaskSection/LookupTaskSection.constants'
import LookupTaskSection from './LookupTaskSection'
import messages from './translate'
import AssignUsersForm from './AssignUsersForm'
import ConditionalLogic from './ConditionalLogic/ConditionalLogic'
import PopulateLogic from './ConditionalLogic/PopulateLogic'
import PreviewInputsForm from '../PreviewInputsForm'

function usePrevious(value) {
  const ref = useRef()
  useEffect(() => {
    ref.current = value
  })

  return ref.current
}

const useStyles = makeStyles({
  image_wrap: {
    position: 'relative',
    marginTop: '30px',
  },
  remove_image: {
    position: 'absolute',
    right: 0,
    bottom: '5px',
    cursor: 'pointer',
  },
  file_input: {
    display: 'none',
  },
  field_input: {
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'pointer',
    padding: '10px',
    border: '1px solid rgba(0, 0, 0, 0.42)',
  },
  field_icon: {
    marginRight: '15px',
  },
})

function TaskForm({
  onSubmit,
  fetchWorkflowList,
  workFlowList,
  formData,
  uploadFiles,
  showNotice,
  fetchAssignData,
  isFormLoading,
  fetchTaskPolicies,
  policyList,
  assignData,
  selected,
  setSelected,
  setIsUserTableOpen,
  isBulkSelect,
  setIsBulkSelect,
  intl: { formatMessage },
  fetchLookupTablesSource,
  lookupTablesSource,
  featureFlags,
}) {
  const initialLanguages = {}

  Object.values(APP_LANGUAGES).forEach(({ key }) => {
    initialLanguages[key] = ''
  })

  let initialValues = {
    title: initialLanguages,
    enabled: true,
    position: 0,
    needs_locus: false,
    needs_locus_group: false,
    needs_multi_unit: false,
    job_approval_task: false,
    optional: false,
    duplicable: false,
    image_path: '',
    youtube_video_link: '',
    workflow: null,
    instructions: initialLanguages,
    policies: [],
    assigned_users: [],
    assigned_user_groups: [],
    conditional_logic_items: [],
    populate_logic_items: [],
  }
  const [imageData, setImageData] = useState({})
  const [isBulkSelectOpen, setIsBulkSelectOpen] = useState(false)
  const [stateFormData, setStateFormData] = useState(initialValues)
  const prevInitialValue = usePrevious(stateFormData)
  const [imagePath, setImagePath] = useState('')
  const [titleChanged, setTitleChanged] = useState(false)
  const [assignChanged, setAssignChanged] = useState(false)
  const [
    activeLocusLookupColumnList,
    setActiveLocusLookupColumnList,
  ] = useState([])
  const [
    activeLocusGroupLookupColumnList,
    setActiveLocusGroupLookupColumnList,
  ] = useState([])
  const [previewInputs, setPreviewInputs] = useState([])
  const classes = useStyles()
  const disabledButton =
    !assignChanged &&
    !titleChanged &&
    (isEqual(stateFormData, initialValues) ||
      isEqual(prevInitialValue, stateFormData))
  const isFormExist = !isEmpty(formData)
  const { needs_locus, needs_locus_group, needs_multi_unit } = stateFormData

  const getArrIds = arr => {
    let result = []

    if (Array.isArray(arr) && arr.length) {
      result = arr.map(item => item.id)
    }

    return result
  }

  useEffect(() => {
    const psfs = new Psfs()
    psfs.addSort('title')

    fetchWorkflowList({ psfs })
    fetchAssignData()
    fetchTaskPolicies()
    fetchLookupTablesSource()
  }, [])

  useEffect(() => {
    setAssignChanged(true)
  }, [selected, isBulkSelect])

  useEffect(() => {
    if (Object.keys(imageData).length) {
      initialValues.image_path = imageData.path
      setImagePath(imageData.path)
      setStateFormData(prevState => ({
        ...prevState,
        image_path: imageData.path,
      }))
    }
  }, [imageData])

  useEffect(() => {
    if (isFormExist) {
      const {
        id,
        image_url,
        workflow: { id: workflowId },
        assigned_user_groups,
        assigned_users,
        policies,
        ...rest
      } = formData
      initialValues = {
        workflow: workflowId,
        ...(Object.keys(rest).length ? rest : initialValues),
      }

      initialValues.workflow = workflowId
      initialValues.assigned_user_groups = getArrIds(assigned_user_groups)
      initialValues.assigned_users = getArrIds(assigned_users)
      initialValues.policies = getArrIds(policies)
      initialValues.conditional_logic_items = Array.isArray(
        rest.conditional_logic_items,
      )
        ? rest.conditional_logic_items
        : []
      initialValues.populate_logic_items = Array.isArray(
        rest.populate_logic_items,
      )
        ? rest.populate_logic_items
        : []
      setStateFormData(initialValues)

      if (rest.image_path) {
        setImagePath(rest.image_path)
      }
    }
  }, [JSON.stringify(formData)])

  useEffect(() => {
    if (!needs_locus_group && !needs_locus && needs_multi_unit) {
      setStateFormData(prevState => ({
        ...prevState,
        needs_multi_unit: false,
      }))
    }
  }, [needs_locus_group, needs_locus])

  useEffect(() => {
    const inputLookupTablesSourceData = lookupTablesSource.toJS()

    if (inputLookupTablesSourceData.length) {
      const { locus_lookup_table, locus_group_lookup_table } = stateFormData
      const { columns: locusColumns } =
        inputLookupTablesSourceData.find(
          ({ id }) => id === locus_lookup_table,
        ) || {}
      const { columns: locusGroupColumns } =
        inputLookupTablesSourceData.find(
          ({ id }) => id === locus_group_lookup_table,
        ) || {}

      if (locusColumns) {
        setActiveLocusLookupColumnList(locusColumns)
      }

      if (locusGroupColumns) {
        setActiveLocusGroupLookupColumnList(locusGroupColumns)
      }
    }
  }, [lookupTablesSource, JSON.stringify(stateFormData)])

  useEffect(() => {
    if (formData.preview_inputs) {
      setPreviewInputs([...formData.preview_inputs])
    }
  }, [formData])

  const handleChange = (val, name) => {
    setStateFormData(prevState => ({
      ...prevState,
      [name]: val,
    }))

    return val
  }

  const handleChangeNeedsLocus = val => {
    setStateFormData(prevState => ({
      ...prevState,
      needs_locus: val,
      [LOCUS_LOOKUP_TABLE]: null,
      [LOCUS_LOOKUP_TABLE_COLUMN]: null,
      [LOCUS_CREATE_NEW_ROW]: false,
    }))

    return val
  }

  const handleChangeNeedsLocusGroup = val => {
    setStateFormData(prevState => ({
      ...prevState,
      needs_locus_group: val,
      [LOCUS_GROUP_LOOKUP_TABLE]: null,
      [LOCUS_GROUP_LOOKUP_TABLE_COLUMN]: null,
      [LOCUS_GROUP_CREATE_NEW_ROW]: false,
    }))

    return val
  }

  const onChangeFile = file => {
    if (!file) return

    const fileForm = new FormData()
    fileForm.append('file', file[0])

    withPromise(uploadFiles, {
      file: fileForm,
      partUrl: MY_JOBS_UPLOAD_FILES,
      formatMessage,
    }).then(response => {
      if (response.error) {
        showNotice({
          notice: NoticeHelper.createErrorObj(
            formatMessage(messages.fileNotUploadMessage),
          ),
        })
      } else {
        setImageData(prevState => ({
          ...prevState,
          ...response,
        }))
      }
    })
  }

  const removeImage = () => {
    setImageData({})
    setImagePath('')
    setStateFormData(prevState => ({
      ...prevState,
      image_path: '',
    }))
    onChangeFile(null)
  }

  const onChangeLocusLookupTable = args => {
    const { columns } = lookupTablesSource.find(
      ({ id: tableId }) => tableId === args.target.value,
    )

    setActiveLocusLookupColumnList(columns)
    setStateFormData(prevState => ({
      ...prevState,
      [LOCUS_LOOKUP_TABLE]: args.target.value,
      [LOCUS_LOOKUP_TABLE_COLUMN]: null,
      [LOCUS_CREATE_NEW_ROW]: false,
    }))
  }

  const onChangeLocusLookupTableColumn = args => {
    const activeColumn = activeLocusLookupColumnList.find(
      ({ key }) => key === args.target.value,
    )

    setStateFormData(prevState => ({
      ...prevState,
      [LOCUS_LOOKUP_TABLE_COLUMN]: activeColumn.key,
    }))
  }

  const onChangeLocusGroupLookupTable = args => {
    const { columns } = lookupTablesSource.find(
      ({ id: tableId }) => tableId === args.target.value,
    )

    setActiveLocusGroupLookupColumnList(columns)
    setStateFormData(prevState => ({
      ...prevState,
      [LOCUS_GROUP_LOOKUP_TABLE]: args.target.value,
      [LOCUS_GROUP_LOOKUP_TABLE_COLUMN]: null,
      [LOCUS_GROUP_CREATE_NEW_ROW]: false,
    }))
  }

  const onChangeLocusGroupLookupTableColumn = args => {
    const activeColumn = activeLocusGroupLookupColumnList.find(
      ({ key }) => key === args.target.value,
    )

    setStateFormData(prevState => ({
      ...prevState,
      [LOCUS_GROUP_LOOKUP_TABLE_COLUMN]: activeColumn.key,
    }))
  }

  return (
    <Form
      onSubmit={values =>
        onSubmit(values, imageData.path || imagePath, previewInputs)
      }
      initialValues={stateFormData}
      {...(featureFlags.INSTRUCTIONS_TEXT_EDITOR
        ? { subscription: { submitting: true, touched: true, invalid: true } }
        : {})}
      mutators={{
        handleMultiLanguageValue: (
          [
            {
              target: { value },
            },
            field,
            key,
          ],
          state,
          { changeValue },
        ) => {
          setStateFormData(prevState => ({
            ...prevState,
            [field]: {
              ...prevState[field],
              [key]: value,
            },
          }))
          changeValue(state, `${field}[${key}]`, () => value)
        },
        handleTitleClear: (value, state, { changeValue }) => {
          setStateFormData(prevState => ({
            ...prevState,
            title: {
              ...prevState.title,
              [value[0]]: '',
            },
          }))
          changeValue(state, `title.${value[0]}`, () => {})
          setTitleChanged(true)
        },
        handleInstructionClear: (value, state, { changeValue }) => {
          setStateFormData(prevState => ({
            ...prevState,
            instructions: {
              ...prevState.instructions,
              [value[0]]: '',
            },
          }))
          changeValue(state, `instructions.${value[0]}`, () => {})
          setTitleChanged(true)
        },
      }}
      render={({ handleSubmit, submitting, form: { mutators } }) => (
        <form onSubmit={handleSubmit} className="form-basic">
          <CircleLoader loading={submitting || isFormLoading} />
          <TitleInput
            handleTitleClear={mutators.handleTitleClear}
            formState={stateFormData.title}>
            {Object.values(APP_LANGUAGES).map(({ key }) => {
              const isEnglish = key === 'en'
              const validators = [VALIDATOR_IS_LESS_THEN_255_SYMBOLS]

              if (isEnglish) {
                validators.push(VALIDATOR_IS_EMPTY)
              }

              return (
                <Field
                  name={`title[${key}]`}
                  key={`${key}_title`}
                  type="text"
                  lang={key}
                  label={`${key} ${formatMessage(messages.title)}`}
                  component={Input}
                  className="form-control"
                  disabled={submitting}
                  required={isEnglish}
                  validate={getValidator(validators)}
                  inputProps={{
                    onChange: e => {
                      mutators.handleMultiLanguageValue(e, 'title', key)
                    },
                  }}
                />
              )
            })}
          </TitleInput>
          <FormControl className="form-group" fullWidth>
            <Field
              name="workflow"
              component={Select}
              label={formatMessage(messages.workflow)}
              disabled={submitting}
              formControlProps={{
                fullWidth: true,
                required: true,
              }}
              validate={getValidator([VALIDATOR_IS_EMPTY])}
              parse={val => handleChange(val, 'workflow')}>
              {workFlowList.map(({ id, title }) => (
                <MenuItem button key={id} value={id}>
                  {title}
                </MenuItem>
              ))}
            </Field>
          </FormControl>
          <AssignUsersForm
            isBulkSelect={isBulkSelect}
            setIsUserTableOpen={setIsUserTableOpen}
            setIsBulkSelect={value => {
              setIsBulkSelect(value)
            }}
            isBulkSelectOpen={isBulkSelectOpen}
            setIsBulkSelectOpen={setIsBulkSelectOpen}
            assignData={assignData}
            selected={selected}
            setSelected={setSelected}
          />
          {featureFlags.CONDITIONAL_INPUTS && formData.id && (
            <Field
              name="conditional_logic_items"
              render={props => (
                <ConditionalLogic formData={formData} {...props} />
              )}
            />
          )}
          {featureFlags.CONDITIONAL_INPUTS && formData.id && (
            <Field
              name="populate_logic_items"
              format={value => (Array.isArray(value) ? value : [])}
              render={props => <PopulateLogic formData={formData} {...props} />}
            />
          )}
          <FormControl className="form-group" fullWidth>
            <Field
              multiple
              name="policies"
              component={Select}
              label={formatMessage(messages.indicators)}
              disabled={submitting}
              parse={val => handleChange(val, 'policies')}
              format={value => value || []}>
              {policyList.map(({ id, name }) => (
                <MenuItem button key={id} value={id}>
                  {name}
                </MenuItem>
              ))}
            </Field>
          </FormControl>
          <TitleInput
            handleTitleClear={mutators.handleInstructionClear}
            formState={stateFormData.instructions}>
            {Object.values(APP_LANGUAGES).map(({ key }) => (
              <Field
                name={`instructions[${key}]`}
                key={`${key}_instructions`}
                type="text"
                lang={key}
                component={
                  featureFlags.INSTRUCTIONS_TEXT_EDITOR
                    ? props => (
                        <TextEditor
                          placeholder={`${key} ${formatMessage(
                            messages.instructions,
                          )}`}
                          {...props}
                        />
                      )
                    : TextArea
                }
                className="form-control"
                disabled={submitting}
                inputProps={{
                  onChange: e => {
                    mutators.handleMultiLanguageValue(e, 'instructions', key)
                  },
                }}
              />
            ))}
          </TitleInput>
          <InputLabel htmlFor="filePicker">
            <Field
              name="image_path"
              label="image"
              className="form-control"
              disabled={submitting}>
              {({ value, onChange, ...input }) => (
                <input
                  {...input}
                  className={classes.file_input}
                  id="filePicker"
                  type="file"
                  name="image_path"
                  accept="image/jpg, image/jpeg, image/png"
                  onChange={({ target }) => onChangeFile(target.files)}
                />
              )}
            </Field>
            {!imagePath && (
              <div className={classes.field_input}>
                <UploadIcon className={classes.field_icon} />
                {formatMessage(messages.uploadPngJpg)}
              </div>
            )}
          </InputLabel>
          {imagePath && (
            <div className={`form-group ${classes.image_wrap}`}>
              <TextField
                fullWidth
                readOnly
                type="text"
                label={formatMessage(messages.imagePath)}
                value={imagePath}
              />
              <ClearIcon
                className={classes.remove_image}
                onClick={removeImage}
              />
            </div>
          )}
          <Field
            name="youtube_video_link"
            type="text"
            label={formatMessage(messages.youtubeVideoLink)}
            component={Input}
            className="form-control"
            disabled={submitting}
            parse={val => handleChange(val, 'youtube_video_link')}
            validate={getValidator(VALIDATOR_IS_YOUTUBE_LINK)}
          />
          <Field
            name="position"
            type="number"
            label={formatMessage(messages.position)}
            component={Input}
            className="form-control"
            disabled={submitting}
            validate={getValidator([VALIDATOR_IS_NUMBER])}
            parse={val => handleChange(val, 'position')}
          />
          <PreviewInputsForm
            previewInputs={previewInputs}
            setPreviewInputs={setPreviewInputs}
            submitting={submitting}
            previewPositionProperty="task_preview_position"
          />
          <div className="form-group">
            <FormControl>
              <FormControlLabel
                label={formatMessage(messages.active)}
                disabled={submitting}
                className="form-label"
                control={
                  <Field
                    name="enabled"
                    component={Checkbox}
                    type="checkbox"
                    color="primary"
                    parse={val => handleChange(val, 'enabled')}
                  />
                }
              />
            </FormControl>
          </div>
          <div className="form-group">
            <FormControl>
              <FormControlLabel
                label={formatMessage(messages.jobApprovalTask)}
                disabled={submitting}
                className="form-label"
                control={
                  <Field
                    name="job_approval_task"
                    component={Checkbox}
                    type="checkbox"
                    color="primary"
                    parse={val => handleChange(val, 'job_approval_task')}
                  />
                }
              />
            </FormControl>
          </div>
          <div className="form-group">
            <FormControl>
              <FormControlLabel
                label={formatMessage(messages.optional)}
                disabled={submitting}
                className="form-label"
                control={
                  <Field
                    name="optional"
                    component={Checkbox}
                    type="checkbox"
                    color="primary"
                    parse={val => handleChange(val, 'optional')}
                  />
                }
              />
            </FormControl>
          </div>
          <div className="form-group">
            <FormControl>
              <FormControlLabel
                label={formatMessage(messages.duplicable)}
                disabled={submitting}
                className="form-label"
                control={
                  <Field
                    name="duplicable"
                    component={Checkbox}
                    type="checkbox"
                    color="primary"
                    parse={val => handleChange(val, 'duplicable')}
                  />
                }
              />
            </FormControl>
          </div>
          <div className="form-group">
            <FormControl>
              <FormControlLabel
                label={formatMessage(messages.needsUnit)}
                disabled={submitting}
                className="form-label"
                control={
                  <Field
                    name="needs_locus"
                    component={Checkbox}
                    type="checkbox"
                    color="primary"
                    parse={handleChangeNeedsLocus}
                  />
                }
              />
            </FormControl>
          </div>
          {stateFormData.needs_locus && (
            <LookupTaskSection
              submitting={submitting}
              onChangeTable={onChangeLocusLookupTable}
              onChangeColumn={onChangeLocusLookupTableColumn}
              handleChange={handleChange}
              tableFieldName={LOCUS_LOOKUP_TABLE}
              columnFieldName={LOCUS_LOOKUP_TABLE_COLUMN}
              createsNewRowFieldName={LOCUS_CREATE_NEW_ROW}
              columnList={activeLocusLookupColumnList}
              tableValue={stateFormData[LOCUS_LOOKUP_TABLE]}
              columnValue={stateFormData[LOCUS_LOOKUP_TABLE_COLUMN]}
              tableLabel={formatMessage(messages.useUnitsFromTable)}
              columnLabel={formatMessage(messages.useUnitsFromColumn)}
            />
          )}
          <div className="form-group">
            <FormControl>
              <FormControlLabel
                label={formatMessage(messages.needsUnitGroup)}
                disabled={submitting}
                className="form-label"
                control={
                  <Field
                    name="needs_locus_group"
                    component={Checkbox}
                    type="checkbox"
                    color="primary"
                    parse={handleChangeNeedsLocusGroup}
                  />
                }
              />
            </FormControl>
          </div>
          {stateFormData.needs_locus_group && (
            <LookupTaskSection
              submitting={submitting}
              onChangeTable={onChangeLocusGroupLookupTable}
              onChangeColumn={onChangeLocusGroupLookupTableColumn}
              handleChange={handleChange}
              tableFieldName={LOCUS_GROUP_LOOKUP_TABLE}
              columnFieldName={LOCUS_GROUP_LOOKUP_TABLE_COLUMN}
              createsNewRowFieldName={LOCUS_GROUP_CREATE_NEW_ROW}
              columnList={activeLocusGroupLookupColumnList}
              tableValue={stateFormData[LOCUS_GROUP_LOOKUP_TABLE]}
              columnValue={stateFormData[LOCUS_GROUP_LOOKUP_TABLE_COLUMN]}
              tableLabel={formatMessage(messages.useUnitGroupsFromTable)}
              columnLabel={formatMessage(messages.useUnitGroupsFromColumn)}
            />
          )}
          <div className="form-group">
            <FormControl>
              <FormControlLabel
                label={formatMessage(messages.multiUnitSelection)}
                disabled={(!needs_locus && !needs_locus_group) || submitting}
                className="form-label"
                control={
                  <Field
                    name="needs_multi_unit"
                    component={Checkbox}
                    type="checkbox"
                    color="primary"
                    parse={val => handleChange(val, 'needs_multi_unit')}
                  />
                }
              />
            </FormControl>
          </div>
          <div className="form-group">
            <Button
              href={null}
              type="submit"
              color="primary"
              size="medium"
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting || disabledButton}>
              {formatMessage(messages.saveInfo)}
            </Button>
          </div>
        </form>
      )}
    />
  )
}

TaskForm.propTypes = {
  formData: PropTypes.object.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
    }),
  }).isRequired,
  history: PropTypes.shape({
    goBack: PropTypes.func,
  }),
  intl: REACT_INTL_PROP_TYPE.isRequired,
  policyList: PropTypes.array.isRequired,
  workFlowList: PropTypes.arrayOf(PropTypes.object).isRequired,
  assignData: PropTypes.object.isRequired,
  fetchTaskFormData: PropTypes.func,
  uploadFiles: PropTypes.func,
  fetchAssignData: PropTypes.func,
  fetchWorkflowList: PropTypes.func,
  fetchTaskPolicies: PropTypes.func,
  fetchLookupTablesSource: PropTypes.func,
  showNotice: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  isFormLoading: PropTypes.bool.isRequired,
}

TaskForm.defaultProps = {
  fetchTaskFormData: () => {},
  uploadFiles: () => {},
  fetchAssignData: () => {},
  fetchWorkflowList: () => {},
  fetchTaskPolicies: () => {},
  fetchLookupTablesSource: () => {},
  showNotice: () => {},
  history: PropTypes.shape({
    goBack: () => {},
  }),
}

export default connect(
  state => ({
    tableData: state.getIn(['jobTasksAdmin', 'tableData']),
    workFlowList: state.getIn(['jobTasksAdmin', 'workflowList']).toJS(),
    isFormLoading: state.getIn(['jobTasksAdmin', 'loading']),
    assignData: state.getIn(['myJobs', 'assignData']).toJS(),
    policyList: state.getIn(['jobTasksAdmin', 'policies']).toArray(),
    lookupTablesSource: state.getIn(['jobInputsAdmin', 'lookupTablesSource']),
    featureFlags: state.getIn(['profile', 'profile', 'featureFlags']),
  }),
  {
    fetchWorkflowList,
    fetchTaskFormData,
    uploadFiles,
    fetchAssignData,
    fetchTaskPolicies,
    fetchLookupTablesSource,
  },
)(withRouter(injectIntl(TaskForm)))
